import { AttachmentRule, Behavior } from "./types";
import { WorldMaker } from "../WorldMaker";

export class DestroyOnDeath extends Behavior {
  private deathDetectedTime: number | null = null;
  private readonly deathDelay: number;

  constructor(element: Element, world: WorldMaker) {
    super(element, world);
    // Get delay from data attribute or default to 1 second
    const delayAttr = element.getAttribute("death-delay");
    this.deathDelay = delayAttr ? parseFloat(delayAttr) * 3000 : 3000; // Convert to milliseconds
  }

  static getAttachmentRules(): AttachmentRule[] {
    return [
      { type: "class", selector: "destroy-on-death" },
      { type: "element", selector: "m-character" },
      // Could also auto-attach to elements with health behavior
      // { type: 'class', selector: 'has-health' }
    ];
  }

  onAttach(): void {
    console.log("DestroyOnDeath attached to:", this.element, `with delay: ${this.deathDelay}ms`);
  }

  onDetach(): void {
    console.log("DestroyOnDeath detached from:", this.element);
  }

  private destroyElement(): void {
    // Check if element is still in the DOM to avoid double-destruction
    if (!this.element.parentNode) {
      console.log("Element already removed from DOM, skipping destruction");
      return;
    }

    console.log("Destroying element due to death class:", this.element);

    // Dispatch a custom event before destruction
    const destroyEvent = new CustomEvent("element-destroyed", {
      detail: {
        element: this.element,
        reason: "death",
        delayTime: this.deathDelay,
      },
      bubbles: true,
    });
    this.element.dispatchEvent(destroyEvent);

    // Remove the element from the DOM
    // The MutationObserver should detect this and clean up behaviors automatically
    this.element.remove();
  }

  tick(deltaTime: number): void {
    // Check if element is still in the DOM first
    if (!this.element.parentNode) {
      return;
    }

    // Check for death-related classes
    const isDead =
      this.element.classList.contains("death") || this.element.classList.contains("dead");

    if (isDead) {
      // If death just detected, record the time
      if (this.deathDetectedTime === null) {
        this.deathDetectedTime = Date.now();
        console.log(
          "Death detected, starting countdown:",
          this.element,
          `delay: ${this.deathDelay}ms`,
        );

        // Add a visual indicator class for the death countdown
        this.element.classList.add("death-countdown");
      }

      // Check if enough time has passed since death was detected
      const elapsedTime = Date.now() - this.deathDetectedTime;
      if (elapsedTime >= this.deathDelay) {
        this.destroyElement();
      }
    } else {
      // Reset death timer if no longer dead (resurrection?)
      if (this.deathDetectedTime !== null) {
        console.log("Element no longer dead, resetting death timer:", this.element);
        this.deathDetectedTime = null;
        this.element.classList.remove("death-countdown");
      }
    }
  }
}
